import React from "react";
import {
  Type,
  useMediaQuery,
  VideoPlayer,
} from "@ebscois/react-component-library";
import { useBaseMediaUrls } from "../../hooks/useBaseMediaUrls";
import { getAnchor } from "../../util/getAnchor";
import {
  THUMB_SMALL,
  THUMB_MEDIUM,
  THUMB_LARGE,
} from "../../constants/image-sizes";
import {
  getImageUrl,
  getVideoUrl,
  transformations,
} from "../../util/get-url-utils";

import {
  MediaCard,
  MediaContent,
  Media,
  MediaImage,
  MediaDescription,
  MediaTitle,
  MediaHeader,
  StyledVideoPlayerPoster,
  MediaCopyright,
} from "./styled";

const InlineMedia = ({
  openMediaHandler,
  title,
  description,
  copyright,
  isMediaClickDisabled,
  publicId,
  mediaType,
  index,
  media,
  maxIndex,
}) => {
  const mqMinSm = useMediaQuery("min", "sm");
  const mediaName = mediaType === "video" ? "Video" : "Image";
  const cloudinaryUrl = useBaseMediaUrls(
    mediaType === "video" ? "videos" : "images"
  );
  const videoAnchor = getAnchor(publicId);

  const handleMediaClick = (event) => {
    event.preventDefault();
    openMediaHandler();
  };

  const splitPublicId = publicId.split("/");
  const splitPublicIdLength = splitPublicId.length;
  return (
    <MediaCard
      id={splitPublicId?.[splitPublicIdLength - 1]}
      backgroundColor="gray200"
      borderColor="gray400"
      borderStyle="solid"
      borderWidth="1px"
      isRow={mqMinSm}
      $hideInlineVideoPrint={mediaType === "video"}
    >
      <Media
        borderColor="gray400"
        borderStyle="solid"
        borderRightWidth={mqMinSm && "1px"}
        borderBottomWidth={!mqMinSm && "1px"}
        onClick={
          isMediaClickDisabled || (!mqMinSm && mediaType === "video")
            ? null
            : handleMediaClick
        }
        isMediaClickDisabled={
          isMediaClickDisabled || (!mqMinSm && mediaType === "video")
        }
        data-testid={
          isMediaClickDisabled
            ? "inline-media-disabled"
            : "inline-media-enabled"
        }
      >
        {mediaType === "video" ? (
          !mqMinSm ? (
            <VideoPlayer
              id={videoAnchor}
              inheritSize
              lazyLoad={false}
              src={getVideoUrl(
                publicId,
                cloudinaryUrl,
                transformations.fullRes
              )}
              poster={`${getVideoUrl(
                publicId,
                cloudinaryUrl,
                transformations.small
              )}.jpg`}
              data-testid="inline-media-video-player"
            />
          ) : (
            <StyledVideoPlayerPoster
              lazyLoad={false}
              url={`${getVideoUrl(
                publicId,
                cloudinaryUrl,
                transformations.small
              )}.jpg`}
              data-testid="inline-media-video-poster"
              alt={title}
            />
          )
        ) : (
          <MediaImage
            alt={title}
            src={getImageUrl(publicId, cloudinaryUrl, transformations.small)}
            data-testid="inline-media-image"
            srcSet={`${getImageUrl(
              publicId,
              cloudinaryUrl,
              transformations.small
            )} ${THUMB_SMALL}, ${getImageUrl(
              publicId,
              cloudinaryUrl,
              transformations.medium
            )} ${THUMB_MEDIUM}, ${getImageUrl(
              publicId,
              cloudinaryUrl,
              transformations.large
            )} ${THUMB_LARGE}`}
          />
        )}
      </Media>
      <MediaContent className="card-content">
        <MediaHeader>
          <div>
            {index >= 0 && (
              <Type
                element="p"
                fontSize="xs"
                fontWeight="normal"
                data-testid="inline-media-index"
              >
                {mediaName} {index + 1} of {maxIndex}
              </Type>
            )}
            <MediaTitle
              element="p"
              fontSize="md"
              marginBottom="xs"
              color="gray800"
              fontWeight="semibold"
            >
              {title}
            </MediaTitle>
          </div>
        </MediaHeader>
        {description && (
          <MediaDescription
            element="p"
            fontSize="sm"
            color="gray800"
            data-testid="inline-media-description"
            marginTop="0"
          >
            {description}
          </MediaDescription>
        )}
        {isMediaClickDisabled && copyright && (
          <MediaCopyright data-testid="inline-media-copyright">
            {copyright}
          </MediaCopyright>
        )}
      </MediaContent>
    </MediaCard>
  );
};

InlineMedia.displayName = "InlineMedia";

export default InlineMedia;
